import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Dialog, DialogPanel, DialogTitle, Description } from '@headlessui/react';
import { ArrowLeftRight, LoaderCircle, RefreshCw } from 'lucide-react';
import { workspaceClient, workspaceLeaveState, workspacePath } from './workspaceClient';
import './workspace-preferences.css';

const WorkspaceContext = createContext(null);

export function WorkspacePreferencesProvider() {
  const [state, setState] = useState({ loading: true, data: null, error: '' });
  const controller = useRef(null);
  const mounted = useRef(false);
  const saving = useRef(false);
  const refresh = useCallback(() => {
    if (saving.current) return;
    controller.current?.abort();
    const request = new AbortController();
    controller.current = request;
    setState(current => ({ ...current, loading: true, error: '' }));
    workspaceClient.get(request.signal).then(data => {
      if (!request.signal.aborted) setState({ loading: false, data, error: '' });
    }).catch(error => {
      if (!request.signal.aborted) setState({ loading: false, data: null, error: error.message });
    });
  }, []);
  useEffect(() => {
    mounted.current = true;
    refresh();
    window.addEventListener('focus', refresh);
    return () => { mounted.current = false; controller.current?.abort(); window.removeEventListener('focus', refresh); };
  }, [refresh]);
  const save = async preference => {
    if (saving.current) throw new Error('Your workspace choice is already being saved.');
    saving.current = true;
    controller.current?.abort();
    try {
      const data = await workspaceClient.save(preference);
      if (data.preference !== preference) throw new Error('Your workspace choice was not saved. Please try again.');
      if (mounted.current) setState({ loading: false, data, error: '' });
      return data;
    } finally {
      saving.current = false;
      if (mounted.current) setState(current => ({ ...current, loading: false }));
    }
  };
  return <WorkspaceContext.Provider value={{ ...state, refresh, save }}><Outlet /></WorkspaceContext.Provider>;
}

export function WorkspaceEntry() {
  const workspace = useContext(WorkspaceContext);
  if (!workspace || workspace.loading) return <main className="io-interface-entry" role="status">Opening your workspace...</main>;
  if (workspace.error) return <main className="io-interface-entry"><div>
    <h1>Choose where to continue</h1><p role="alert">We could not load your saved workspace choice.</p>
    <button className="io-interface-secondary" onClick={workspace.refresh}><RefreshCw size={16} />Try again</button>
    <Link to="/dashboard">Open classic workspace</Link>
  </div></main>;
  return <Navigate replace to={workspace.data.activeInterface === 'focus' ? '/focus' : '/dashboard'} />;
}

export function WorkspaceSwitch({ to, className = '', compact = false, onSwitched }) {
  const workspace = useContext(WorkspaceContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const lock = useRef(false);
  const keepRef = useRef(null);
  const label = to === 'classic' ? 'Switch to classic' : 'Switch to new workspace';
  if (!workspace) return null;
  const unavailable = to === 'focus' && !workspace.data?.focusAvailable;
  const start = () => { setError(''); setOpen(true); };
  const switchWorkspace = async () => {
    if (lock.current) return;
    if (workspaceLeaveState().busy) { setError('Wait for your current action to finish before switching.'); return; }
    lock.current = true; setBusy(true); setError('');
    try {
      const saved = await workspace.save(to);
      if (saved.activeInterface !== to) throw new Error('The new workspace is temporarily unavailable. Classic is still available.');
      setOpen(false);
      onSwitched?.();
      navigate(workspacePath(location, to));
    } catch (err) { setError(err.message || 'Could not save your choice. Please try again.'); }
    finally { lock.current = false; setBusy(false); }
  };
  return <>
    <button type="button" className={`io-interface-switch ${className}`} title={unavailable ? 'The new workspace is currently unavailable' : label}
      aria-label={label} disabled={unavailable} onClick={start}>
      <ArrowLeftRight size={17} aria-hidden="true" /><span className={compact ? 'io-interface-label-hidden' : undefined}>{label}</span>
    </button>
    <Dialog open={open} onClose={() => { if (!busy) setOpen(false); }} initialFocus={keepRef} className="io-interface-dialog" data-workspace-switch-dialog="">
      <div className="io-interface-backdrop" aria-hidden="true" />
      <div className="io-interface-position"><DialogPanel className="io-interface-panel">
        <DialogTitle>{label}?</DialogTitle>
        <Description>Your saved emails, meetings, and tasks stay the same. We will remember this choice for your next sign-in, on any device.</Description>
        <p className="io-interface-warning">Save any unfinished work first. Unsaved edits on this page will be discarded.</p>
        {error && <p role="alert" className="io-interface-error">{error}</p>}
        <footer>
          <button ref={keepRef} className="io-interface-secondary" disabled={busy} onClick={() => setOpen(false)}>Stay here</button>
          <button className="io-interface-primary" disabled={busy} onClick={switchWorkspace}>
            {busy ? <LoaderCircle size={16} aria-hidden="true" /> : <ArrowLeftRight size={16} aria-hidden="true" />}
            {busy ? 'Saving choice...' : label}
          </button>
        </footer>
      </DialogPanel></div>
    </Dialog>
  </>;
}
